import { useState, useEffect, useCallback, useMemo } from 'react'
import { getMatrizEntrega, getAllGroups, getOrgDeportes, getAllStaffIncludingInactive } from '../../../../lib/supabase'
import { useOrganization } from '../../../../contexts/OrganizationContext'
import { useTemporadaActiva } from '../../pagos/useTemporadaActiva'
import { useInscritosTemporada } from '../../../../hooks/useInscritosTemporada'

const STORAGE_KEY = 'entregas_temporada_id'

// Carga compartida por las 3 pantallas de Entregas (Lista, Tablero, Registrar).
// La temporada elegida se persiste en localStorage para que se mantenga al
// navegar entre rutas (useTemporadaActiva por sí solo se reinicia a la abierta).
export const useEntregasData = () => {
  const { org } = useOrganization()
  const { temporadas, temporadaId: defaultTemporadaId } = useTemporadaActiva()

  const [temporadaId, setTemporadaIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null } catch { return null }
  })
  const [matriz, setMatriz] = useState({ prendas: [], jugadores: [], entregas: [] })
  const [grupos, setGrupos] = useState([])
  const [orgDeportes, setOrgDeportes] = useState([])
  const [staffById, setStaffById] = useState({})
  const [loading, setLoading] = useState(true)

  // Adoptar la temporada por defecto (abierta) si no hay selección persistida,
  // o si la persistida ya no existe en el catálogo cargado.
  useEffect(() => {
    if (!temporadas.length) return
    const existe = temporadaId && temporadas.some((t) => t.id === temporadaId)
    if (!existe && defaultTemporadaId) setTemporadaIdState(defaultTemporadaId)
  }, [temporadas, defaultTemporadaId, temporadaId])

  const setTemporadaId = useCallback((id) => {
    setTemporadaIdState(id)
    try { localStorage.setItem(STORAGE_KEY, id) } catch { /* ignore */ }
  }, [])

  const load = useCallback(async (silent = false) => {
    if (!temporadaId) {
      // Sin temporada no hay nada que cargar, pero las pantallas siguen
      // esperando "loading" para saber cuándo mostrar su estado vacío.
      if (!silent) setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    const [matrizRes, gruposRes, deportesRes, staffRes] = await Promise.all([
      getMatrizEntrega(temporadaId, {}),
      getAllGroups(),
      getOrgDeportes(org?.id),
      getAllStaffIncludingInactive(),
    ])
    if (matrizRes.data) setMatriz(matrizRes.data)
    setGrupos((gruposRes.data || []).filter((g) => g.estatus === 'Activo'))
    setOrgDeportes(deportesRes.data || [])
    setStaffById(Object.fromEntries((staffRes.data || []).map((s) => [s.id, s.nombre_completo])))
    if (!silent) setLoading(false)
  }, [temporadaId, org?.id])

  useEffect(() => { load() }, [load])

  // El padron de la temporada, compartido por las pantallas de Entregas para que
  // Lista, Tablero y Estacion cuadren entre si.
  //
  // NO se filtra la matriz aqui: `jugadores` es el diccionario con el que las
  // pantallas resuelven nombre y grupo de cada fila (recortarlo dejaria filas
  // huerfanas), y hay listas que NO deben filtrarse -- el detalle de un pedido ya
  // hecho tiene que mostrar lo que se ordeno, aunque ese jugador se haya ido
  // despues. Cada pantalla aplica `entregasDelPadron` donde corresponde.
  const inscritosEstado = useInscritosTemporada(temporadaId)
  const entregasDelPadron = useMemo(
    () => inscritosEstado.filtrar(matriz.entregas || [], (e) => e.jugador_id),
    [matriz.entregas, inscritosEstado],
  )

  return {
    temporadas, temporadaId, setTemporadaId,
    matriz, grupos, orgDeportes, staffById, loading,
    inscritosEstado, entregasDelPadron,
    reload: () => load(true),
  }
}
